import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { usePSNavigate } from '../../../hooks/usePSNavigate'

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
    ({ className, ...props }, ref) => (
        <div className="relative w-full overflow-x-auto rounded-lg border border-gray-300">
            <table ref={ref} className={twMerge('w-full text-sm', className)} {...props} />
        </div>
    )
)
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
    <thead
        ref={ref}
        className={twMerge('border-gray-300 bg-gray-100 [&_tr]:border-b', className)}
        {...props}
    />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
    <tbody ref={ref} className={twMerge('[&_tr:last-child]:border-0', className)} {...props} />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
    <tfoot
        ref={ref}
        className={twMerge(
            'border-t border-gray-300 font-bold text-gray-700 last:[&>tr]:border-b-0',
            className
        )}
        {...props}
    />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement> & { location?: 'head' | 'body' }
>(({ className, location = 'body', ...props }, ref) => (
    <tr
        ref={ref}
        className={twMerge(
            'border-b border-gray-300 transition-colors',
            location === 'body' && 'hover:bg-black/2',
            location === 'body' ? 'group/body-table-row' : 'group/head-table-row',
            className
        )}
        {...props}
    />
))
TableRow.displayName = 'TableRow'

const ClickableTableRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement> & {
        location?: 'head' | 'body'
        href: string
        target?: string
    }
>(({ className, location = 'body', href, target, ...props }, ref) => {
    const navigate = usePSNavigate()
    return (
        <tr
            ref={ref}
            onMouseUp={(e) => {
                // support middle click opening new tab
                if (e.button === 1) {
                    window.open(href, target)
                }
            }}
            onClick={(e) => {
                // support cmd/ctrl click opening new tab
                if (e.metaKey || e.ctrlKey) {
                    window.open(href, target)
                }
                else if (target === '_blank') {
                    window.open(href, target)
                }
                else {
                    navigate(href)
                }
            }}
            className={twMerge(
                'cursor-pointer border-b border-gray-300 transition-colors',
                location === 'body' && 'hover:bg-black/4',
                location === 'body' ? 'group/body-table-row' : 'group/head-table-row',
                className
            )}
            {...props}
        />
    )
})
ClickableTableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
    <th
        ref={ref}
        className={twMerge('h-12 px-6 text-left align-middle text-gray-600', className)}
        {...props}
    />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
    <td ref={ref} className={twMerge('px-6 py-4 align-middle', className)} {...props} />
))
TableCell.displayName = 'TableCell'

export {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
    ClickableTableRow,
}
